<template>
  <EditPostModal
    v-if="noteDialogVisable"
    :id="id"
    :open="noteDialogVisable"
    :post-to-edit="postToEdit"
    :is-rtl="rtl"
    @close="closeEditPostModal"
    @publish-new-post="sendNewPost"
    @publish-update-post="editPost"
  />

  <ShowPostModal
    v-if="postToEdit"
    :open="showPost"
    :post-to-edit="postToEdit"
    :current-user="currentUser"
    :is-operator="isOperator"
    :is-rtl="rtl"
    :demo="demo"
    :is-demo-post="isDemoPost(postToEdit?.post_id)"
    @close="closePostModal"
    @comment="addComment"
    @delete-comment="deleteComment"
    @edit-comment="editComment"
    @add-like="addLikeToPost"
  />

  <div
    class="collab-board-block p-5 rounded-xl relative"
    :style="{
      direction: rtl ? 'rtl' : 'ltr',
      backgroundColor,
    }"
  >
    <template v-if="true">
      <button
        v-if="!posts.length || (!demoPosts.length && isCanvas)"
        class="flex items-center justify-start px-3 bg-[#ffff] bg-opacity-50 border-2 border-secondary-250 py-2.5 rounded-lg w-[200px] create-note-button"
        :class="{ 'hover:opacity-100': !readOnly }"
        @click="
          postToEdit = null;
          noteDialogVisable = true;
        "
      >
        <Plus class="h-4 stroke-secondary-975" />
        <span class="text-sm !text-secondary-975">{{ newNoteText }}</span>
      </button>
      <MasonryWall
        v-if="layoutReady"
        :key="`masonry-${rtl}-${layoutKey}`"
        :items="sortedPosts"
        :ssr-columns="4"
        :min-columns="4"
        :max-columns="4"
        :column-width="200"
        :gap="16"
      >
        <template #default="{ item, index }">
          <button
            v-if="index === 0"
            class="flex items-center justify-start mb-4 px-3 w-full bg-opacity-50 border-2 border-secondary-250 py-2.5 rounded-lg create-note-button hover:opacity-100"
            @click="
              postToEdit = null;
              noteDialogVisable = true;
            "
          >
            <Plus class="h-4 stroke-secondary-975" />
            <span class="text-sm !text-secondary-975">{{ newNoteText }}</span>
          </button>
          <CollaborativePost
            :post="item as Post"
            :current-user="currentUser"
            :editable="postEditable((item as Post)?.author.userId)"
            :post-background="postBackground"
            :is-operator="isOperator"
            :is-demo-post="isDemoPost((item as Post)?.post_id)"
            :is-canvas="isCanvas"
            :block-id="id"
            :is-rtl="rtl"
            @delete="deletePost"
            @edit="handleEditPostclick"
            @click-post="handlePostClick"
            @add-like="addLikeToPost"
            @update-owner-name="handleOwnerNameUpdate"
          />
        </template>
      </MasonryWall>
    </template>
    <div
      v-if="isCanvas"
      class="bg-white absolute bottom-5 z-[100] rounded-md"
      :class="{ 'right-5': rtl, 'left-5': !rtl }"
    >
      <div class="relative rounded-md">
        <button
          class="flex gap-2 items-center py-[0.375rem] px-2 hover:bg-[#F4F7FB] rounded-md cursor-pointer"
          @click="showColorPicker = !showColorPicker"
        >
          <span class="text-normal font-semibold text-secondary-975">
            {{
              rtl
                ? t('editor.pick_collab_background_hebrew')
                : 'Choose Background'
            }}
          </span>
          <div
            class="text-white w-[2.125rem] h-[2.125rem] p-[6px] border-1 border-solid border-secondary-300 rounded-sm"
          >
            <div
              class="w-full h-full rounded-sm"
              :style="{ backgroundColor: selectedColor ?? '#d2bea8' }"
            />
          </div>
        </button>
        <div
          v-if="showColorPicker"
          ref="colorPickerContainer"
          class="absolute top-full mt-2 bg-white shadow-lg rounded-lg z-[200]"
          :class="{ 'right-0': rtl, 'left-0': !rtl }"
        >
          <div
            :title="
              rtl ? t('editor.pick_collab_pallete_hebrew') : 'color pallete'
            "
          >
            <ColorPicker
              :colors="backgroundColors"
              :extended-color-selection="true"
              :main-wrapper-class="'p-3 rounded-md'"
              :button-class="'w-6 h-6 rounded-sm'"
              :rtl="rtl"
              v-model:selectedColor="selectedColor"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch, computed, PropType, nextTick } from 'vue';

import EditPostModal from './EditPostModal.vue';
import ShowPostModal from './ShowPostModal.vue';
import CollaborativePost from './CollaborativePost.vue';
import { Plus } from 'lucide-vue-next';
import { useChannel } from '../composables/useChannel.ts';
import { Post, PostData } from '../types/post.ts';
import MasonryWall from '@yeger/vue-masonry-wall';
import ColorPicker from './ColorPicker.vue';
import { useI18n } from 'vue-i18n';
import { onClickOutside } from '@vueuse/core';
import { usePresence } from '../../../../src/apps/slideshow/usePresence';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    default: '',
  },
  subTitle: {
    type: String,
    default: '',
  },
  currentUser: {
    type: Object as PropType<{ id: string; name: string }>,
    default: '',
  },
  channeUrl: {
    type: String,
    default: '',
  },
  hideContent: {
    type: Boolean,
    default: false,
  },
  demo: {
    type: Boolean,
    default: false,
  },
  readOnly: {
    type: Boolean,
    default: false,
  },
  demoPosts: {
    type: Array as PropType<Post[]>,
    default: () => [],
  },
  isCanvas: {
    type: Boolean,
    default: false,
  },
  rtl: {
    type: Boolean,
    default: false,
  },
  backgroundColor: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['update-owner-name', 'change-background-color']);

const { t } = useI18n();

const selectedColor = ref(props.backgroundColor);

const backgroundColors = [
  '#d2bea8',
  '#E6F4EA',
  '#FCE7F3',
  '#ECFCCB',
  '#FFD8A8',
  '#A8E6CF',
  '#D0E8F2',
  '#B8B8FF',
  '#CFFAFE',
];

const showColorPicker = ref(false);
const colorPickerContainer = ref(null);

onClickOutside(colorPickerContainer, e => {
  const isInsideDropdown = (e.target as HTMLElement).closest(
    '#dropdown-content',
  );

  if (!isInsideDropdown && colorPickerContainer.value) {
    showColorPicker.value = false;
  }
});

const appIdTemp = (import.meta as any).env.VITE_SENDBIRD_APP_ID;
const tokenTemp = (import.meta as any).env.VITE_SENDBIRD_API_TOKEN;
const showPost = ref(false);
const postBackground = ref('#FDF4D8');
const newNoteText = computed(() => (props.rtl ? 'פתק חדש' : 'New Note'));

const {
  posts,
  sendMessage,
  removeMessage,
  updateMessage,
  reactToMessage,
  setChannel,
  login,
  setIsTest,
  connected,
  isOperator,
  saveDemoPosts,
  updateDemoPosts,
} = useChannel(props.channeUrl, appIdTemp, tokenTemp, props.currentUser.id);

const isDemoPost = (postId: string | undefined): boolean => {
  if (!postId) return false;
  return props.demoPosts.some(
    demoPost => (demoPost as Post).post_id === postId,
  );
};

const sortedPosts = computed(() => {
  const demoPosts = [...props.demoPosts].reverse();
  const nonDemoPosts = posts.value.filter(
    post =>
      !demoPosts.some(demoPost => (demoPost as Post).post_id === post.post_id),
  );

  const sortedNonDemoPosts = nonDemoPosts.sort((postA, postB) => {
    const isOperatorA = postA.author.role === 'operator';
    const isOperatorB = postB.author.role === 'operator';
    const editableA = postEditable(postA.author.userId);
    const editableB = postEditable(postB.author.userId);

    if (isOperatorA !== isOperatorB) {
      return isOperatorA ? -1 : 1;
    }
    if (editableA !== editableB) {
      return editableA ? -1 : 1;
    }
    return 0;
  });

  return [...demoPosts, ...sortedNonDemoPosts];
});

const noteDialogVisable = ref(false);
const postToEdit = ref<Post | null>(null);

const sendNewPost = data => {
  const { post, id } = data;
  sendMessage(post, null, props.demoPosts as Post[], id, props.isCanvas);
};

const handlePostClick = (clicked, post: Post) => {
  showPost.value = clicked;
  postToEdit.value = post;
};

const deletePost = data => {
  const { post, id } = data;
  removeMessage(post, id, props.isCanvas);
};
const editPost = data => {
  const { post, id } = data;
  updateMessage(post, props.isCanvas, id);
};
const handleEditPostclick = (post: PostData | null) => {
  noteDialogVisable.value = true;
  postToEdit.value = posts.value.find(
    p => p.message_id === post?.message_id,
  ) as Post;
};

const addComment = (comment: { post: PostData; parentId: number }) => {
  sendMessage(comment.post, comment.parentId, [], '', props.isCanvas);
};
const deleteComment = (comment: PostData) => {
  removeMessage(comment);
};
const editComment = (comment: PostData) => {
  updateMessage(comment, props.isCanvas);
};

const addLikeToPost = (messageId, cancelReaction) => {
  reactToMessage(messageId, cancelReaction, 'like', props.currentUser.id);
};

const closeEditPostModal = () => {
  noteDialogVisable.value = false;
  postToEdit.value = null;
};

const closePostModal = () => {
  showPost.value = false;
  postToEdit.value = null;
};

const postEditable = (authorId: string): boolean => {
  return authorId === props.currentUser.id;
};

const layoutReady = ref(false);
const layoutKey = ref(0);

const initializeLayout = async () => {
  await nextTick();
  layoutKey.value += 1;
};

onMounted(async () => {
  // Setup data first
  if (props.demoPosts.length > 0) {
    saveDemoPosts(props.demoPosts as Post[]);
  }
  if (props.currentUser.id) {
    login(props.currentUser.id);
    setChannel(props.channeUrl);
  }
  setIsTest(props.demo);

  // Then initialize layout without delay
  await nextTick();
  layoutReady.value = true;
});

// Handle slide transitions for proper direction updates
const { onSlideTransitionEnd } = usePresence();
onSlideTransitionEnd(() => {
  if (layoutReady.value) {
    setTimeout(() => initializeLayout(), 100);
  }
});

const handleOwnerNameUpdate = ({ name, id }: { name: string; id: string }) => {
  emit('update-owner-name', { name, id });
};

watch(selectedColor, () => {
  emit('change-background-color', selectedColor.value);
});

watch(
  posts,
  () => {
    if (postToEdit.value) {
      postToEdit.value = posts.value.find(
        post => post.message_id === postToEdit.value?.message_id,
      ) as Post;
    }
  },
  {
    deep: true,
  },
);
watch(
  () => props.demoPosts,
  newDemoPosts => {
    if (newDemoPosts.length > 0) {
      updateDemoPosts(newDemoPosts as Post[]);
    }
  },
  { deep: true },
);
watch(
  () => props.channeUrl,
  () => {
    setChannel(props.channeUrl);
  },
);
watch(
  () => props.currentUser.id,
  () => {
    login(props.currentUser.id);
  },
);
watch(
  () => props.demo,
  () => {
    setIsTest(props.demo);
  },
);

watch(
  () => props.rtl,
  (newRtl, oldRtl) => {
    // Only update if rtl actually changed
    if (newRtl !== oldRtl && layoutReady.value) {
      setTimeout(() => initializeLayout(), 50);
    }
  },
);
</script>

<style scoped>
.create-note-button {
  background-color: #ffff;
  opacity: 0.6;
}

.collab-board-block {
  height: auto;
  min-height: 500px;
}
</style>
